"use client";

import {
  type Activity,
  type UINode,
  currentPage,
  pageProgram,
} from "@/lib/whiteboard/generated/contract";
import {
  type ExerciseController,
  controllerKey,
  controllers,
} from "@/lib/whiteboard/generated/controller";
import React, { useEffect, useRef, useState, useSyncExternalStore } from "react";

const button =
  "min-h-11 rounded-lg border border-[#3A3A3C] px-3 py-2 text-sm focus-visible:outline-2 focus-visible:outline-[#FD954F] disabled:opacity-60";
function ActivityControl({
  activity: a,
  controller: c,
  part = "activity",
}: {
  activity: Activity;
  controller: ExerciseController;
  part?: string;
}) {
  const { state } = useSyncExternalStore(c.subscribe, c.getSnapshot, c.getSnapshot);
  const order = state.orders?.[a.id] || [];
  const [pool] = useState(() => c.pool(a.id));
  const answer = state.answers[a.id] || "";
  const result = state.results[a.id];
  const locked = result === "correct";
  const updateOrder = (next: number[]) => {
    c.order(a.id, next);
  };
  const controls = (
    <>
      {(part === "activity" || part === "input") && (
        <>
          {a.mode === "choice" ? (
            <div className="flex flex-wrap gap-2">
              {a.options.map((option) => (
                <button
                  key={option}
                  type="button"
                  className={`${button} ${answer === option ? "border-[#FD954F] bg-[#FD954F]/10" : ""}`}
                  disabled={locked}
                  aria-pressed={answer === option}
                  onClick={() => c.answer(a.id, option)}
                >
                  {option}
                </button>
              ))}
            </div>
          ) : a.mode === "sequence" ? (
            <div className="space-y-3">
              <p aria-live="polite" className="min-h-6">
                {answer || "Build the sentence"}
              </p>
              <div className="flex flex-wrap gap-2">
                {pool.map((i) => (
                  <button
                    type="button"
                    key={i}
                    className={button}
                    disabled={locked || order.includes(i)}
                    onClick={() => updateOrder([...order, i])}
                  >
                    {a.options[i]}
                  </button>
                ))}
              </div>
              <button
                type="button"
                className={button}
                disabled={locked || !answer}
                onClick={() => updateOrder([])}
              >
                Reset order
              </button>
            </div>
          ) : (
            <input
              aria-label={a.prompt}
              aria-invalid={result === "incorrect"}
              value={answer}
              disabled={locked}
              maxLength={2000}
              onFocus={() => c.editingField(a.id, true)}
              onBlur={() => c.editingField(a.id, false)}
              onChange={(e) => c.answer(a.id, e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.nativeEvent.isComposing) c.check(a.id);
              }}
              className="min-h-11 w-full rounded-lg border border-[#3A3A3C] bg-[#0F0F14] px-3 text-base outline-offset-2 focus:outline-[#FD954F]"
            />
          )}
        </>
      )}
      <div className="flex flex-wrap gap-2">
        {(part === "activity" || part === "check") && (
          <button
            type="button"
            className={`${button} bg-[#FD954F] font-semibold text-[#1C1C1E]`}
            disabled={locked || !answer.trim()}
            onClick={() => c.check(a.id)}
          >
            {locked ? "Correct" : a.mode === "open" ? "Discuss with Chicky" : "Check"}
          </button>
        )}
        {(part === "activity" || part === "hint") && a.hint && (
          <button type="button" className={button} onClick={() => c.hint(a.id)}>
            Hint
          </button>
        )}
      </div>
      {(part === "activity" || part === "hint") && state.hints[a.id] && (
        <p className="text-sm text-[#AEAEB2]">{a.hint}</p>
      )}
      {(part === "activity" || part === "feedback") && result && (
        <p role="status" className={result === "incorrect" ? "text-red-300" : "text-[#AEAEB2]"}>
          {result === "correct"
            ? "Correct. "
            : result === "pending"
              ? "Chicky can discuss your answer. "
              : "Try again. "}
          {result !== "pending" ? a.explanation : ""}
        </p>
      )}
    </>
  );
  return part === "activity" ? (
    <fieldset
      className="min-w-0 space-y-3 rounded-lg border border-[#3A3A3C] p-4"
      data-activity={a.id}
    >
      <legend className="px-1 font-medium">{a.prompt}</legend>
      {controls}
    </fieldset>
  ) : (
    <div className="min-w-0 space-y-2">{controls}</div>
  );
}

function Tree({
  node,
  controller,
}: {
  node: UINode | string;
  controller: ExerciseController;
}): React.ReactNode {
  if (typeof node === "string") return node;
  const p = node.props;
  const children = node.children.map((n, i) => (
    <Tree
      key={typeof n === "string" ? i : String(n.props.key || i)}
      node={n}
      controller={controller}
    />
  ));
  const tone = (
    {
      normal: "text-[#F9F9F9]",
      muted: "text-[#AEAEB2]",
      accent: "text-[#FD954F]",
      success: "text-green-300",
      error: "text-red-300",
    } as Record<string, string>
  )[String(p.tone || "normal")];
  const size = p.size === "small" ? "text-sm" : p.size === "large" ? "text-xl" : "text-base";
  const gap = p.gap === "small" ? "gap-2" : p.gap === "large" ? "gap-6" : "gap-4";
  const cls = `${tone} ${size} min-w-0 break-words`;
  switch (node.tag) {
    case "input":
    case "check":
    case "hint":
    case "feedback":
    case "activity": {
      const a = controller.snapshot.artifact.program.activities.find((v) => v.id === p.activity)!;
      return <ActivityControl key={a.id} activity={a} controller={controller} part={node.tag} />;
    }
    case "button":
      return (
        <button
          type="button"
          className={`${button} ${tone}`}
          onClick={() => controller.action(String(p.action), p.value)}
        >
          {String(p.label)}
        </button>
      );
    case "heading":
      return <h3 className={`${cls} font-semibold`}>{children}</h3>;
    case "strong":
      return <strong className={cls}>{children}</strong>;
    case "em":
      return <em className={cls}>{children}</em>;
    case "mark":
      return <mark className="rounded bg-[#FD954F]/15 px-1 text-[#FD954F]">{children}</mark>;
    case "divider":
      return <hr className="border-[#3A3A3C]" />;
    case "text":
      return <p className={`${cls} whitespace-pre-wrap leading-relaxed`}>{children}</p>;
    case "table":
      return (
        <div className="overflow-x-auto">
          <table className={`w-full ${cls}`}>
            <tbody>{children}</tbody>
          </table>
        </div>
      );
    case "tr":
      return <tr className="border-b border-[#3A3A3C]">{children}</tr>;
    case "td":
      return <td className={`${cls} p-2 align-top`}>{children}</td>;
    default:
      return (
        <div
          className={`${cls} ${gap} ${node.tag === "row" ? "flex flex-wrap items-center" : node.tag === "grid" ? `grid grid-cols-1 ${p.columns === 3 ? "sm:grid-cols-3" : p.columns === 1 ? "" : "sm:grid-cols-2"}` : "flex flex-col"} ${node.tag === "panel" ? "rounded-lg border border-[#3A3A3C] bg-[#1B1C22] p-4" : ""}`}
        >
          {children}
        </div>
      );
  }
}

function GeneratedExerciseContent({ controller }: { controller: ExerciseController }) {
  const s = useSyncExternalStore(
    controller.subscribe,
    controller.getSnapshot,
    controller.getSnapshot
  );
  const page = currentPage(s.artifact.program, s.state);
  const fallback = controller.isFallback();
  const scoped = pageProgram(s.artifact.program, s.state);
  const index = page ? s.artifact.program.pages!.indexOf(page) : 0;
  const pane = useRef<HTMLDivElement>(null);
  const body = useRef<HTMLDivElement>(null);
  const heading = useRef<HTMLHeadingElement>(null);
  const priorPage = useRef(page?.id);
  useEffect(() => {
    if (!s.hidden) controller.mounted();
    if (priorPage.current !== page?.id) {
      if (pane.current) pane.current.scrollTop = 0;
      heading.current?.focus({ preventScroll: true });
      priorPage.current = page?.id;
    }
  }, [controller, s.artifact.revision, page?.id, s.hidden]);
  useEffect(() => {
    if (s.hidden) return;
    controller.visibility(true);
    return () => controller.visibility(false);
  }, [controller, s.hidden]);
  useEffect(() => {
    if (!page || fallback || !s.tree || s.hidden || typeof ResizeObserver === "undefined") return;
    const measure = () => {
      const container = pane.current;
      if (
        container &&
        container.clientHeight > 0 &&
        (container.scrollHeight > container.clientHeight + 8 ||
          container.scrollWidth > container.clientWidth + 8)
      )
        controller.overflow();
    };
    const observer = new ResizeObserver(measure);
    if (pane.current) observer.observe(pane.current);
    if (body.current) observer.observe(body.current);
    const frame = requestAnimationFrame(measure);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
    };
  }, [controller, page, fallback, s.tree, s.hidden]);
  if (s.hidden)
    return (
      <section className="bg-[#15161B] p-4 text-[#F9F9F9]">
        <p className="mb-3">Your practice is saved for this conversation.</p>
        <button className={button} onClick={() => controller.reopen()}>
          Return to exercise
        </button>
      </section>
    );
  return (
    <section
      className={`${page ? "flex h-full min-h-0 flex-col gap-3" : "max-h-full space-y-5 overflow-auto"} bg-[#15161B] p-4 text-[#F9F9F9] sm:p-5`}
      lang={s.artifact.program.language}
    >
      <header className="shrink-0 space-y-1 border-l-2 border-[#FD954F] pl-3">
        <p className="text-xs font-medium uppercase tracking-wide text-[#AEAEB2]">
          {s.artifact.program.level} · Practice
        </p>
        <h2 ref={heading} tabIndex={-1} className="text-xl font-semibold outline-none">
          {page?.title ?? s.artifact.program.title}
        </h2>
        <p className="text-sm text-[#AEAEB2]">
          {page?.instruction ?? s.artifact.program.objective}
        </p>
      </header>
      <div
        ref={pane}
        data-testid="exercise-page-content"
        className={page ? "min-h-0 flex-1 overflow-auto overscroll-contain" : ""}
      >
        <div ref={body}>
          {fallback || !s.tree ? (
            <div className="space-y-4">
              {scoped.passage && (
                <p className="whitespace-pre-wrap leading-relaxed">{scoped.passage}</p>
              )}
              {scoped.activities.map((a) => (
                <ActivityControl key={a.id} activity={a} controller={controller} />
              ))}
            </div>
          ) : (
            <Tree node={s.tree} controller={controller} />
          )}
        </div>
      </div>
      {page && (
        <nav
          aria-label="Exercise pages"
          className="flex shrink-0 items-center justify-between gap-2 border-t border-[#3A3A3C] pt-3"
        >
          <button
            type="button"
            className={button}
            disabled={index === 0}
            onClick={() => controller.navigate(-1)}
            aria-label="Previous page"
          >
            ← Back
          </button>
          <p role="status" className="text-center text-xs text-[#AEAEB2]">
            {index + 1} of {s.artifact.program.pages!.length}
            <span className="block">
              {page.activities.every((id) => s.state.results[id] === "correct")
                ? "Completed"
                : "Practice"}
            </span>
          </p>
          {index + 1 < s.artifact.program.pages!.length ? (
            <button
              type="button"
              className={button}
              onClick={() => controller.navigate(1)}
              aria-label="Next page"
            >
              Next →
            </button>
          ) : (
            <button
              type="button"
              className={button}
              onClick={() => controller.returnToConversation()}
            >
              Back to conversation
            </button>
          )}
        </nav>
      )}
    </section>
  );
}
class GeneratedBoundary extends React.Component<
  { controller: ExerciseController; children: React.ReactNode },
  { failed: boolean }
> {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  componentDidCatch() {
    this.props.controller.fallback("Host renderer failed");
  }
  render() {
    return this.state.failed ? (
      <div className="space-y-3 p-4 text-[#F9F9F9]">
        <p>The exercise layout could not be displayed. You can keep talking with Chicky.</p>
        <button className={button} onClick={() => this.setState({ failed: false })}>
          Try the simplified exercise
        </button>
      </div>
    ) : (
      this.props.children
    );
  }
}
export function GeneratedExercise({ controller }: { controller: ExerciseController }) {
  return (
    <GeneratedBoundary key={controllerKey(controller.snapshot.artifact)} controller={controller}>
      <GeneratedExerciseContent controller={controller} />
    </GeneratedBoundary>
  );
}
export function GeneratedCard({ reference }: { reference: { session: string; id: string } }) {
  const controller = controllers.get(controllerKey(reference));
  return controller ? (
    <GeneratedExercise controller={controller} />
  ) : (
    <p className="p-4 text-[#AEAEB2]">
      This exercise is no longer active. Ask Chicky for a new one.
    </p>
  );
}
